import { createContext, useContext } from 'react';

import type {
  AccountRole,
  AdminUserCreate,
  AdminUserUpdate,
  AuthUser,
  PasswordResetRequestResponse,
  UserPage,
} from './api';

export type AuthenticationMode =
  | 'checking'
  | 'error'
  | 'hosted-anonymous'
  | 'hosted-authenticated'
  | 'hosted-suspended'
  | 'local';

export interface AuthenticationRevalidationState {
  attempt: number;
  message: string | null;
  status: 'checking' | 'failed' | 'idle' | 'retrying';
}

export interface AuthenticationState {
  clearPasswordReset: () => void;
  confirmPasswordReset: (password: string) => Promise<void>;
  createUser: (payload: AdminUserCreate) => Promise<AuthUser>;
  error: string | null;
  listUsers: () => Promise<UserPage>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  mode: AuthenticationMode;
  passwordResetComplete: boolean;
  passwordResetToken: string | null;
  requestPasswordReset: (email: string) => Promise<PasswordResetRequestResponse>;
  revalidation: AuthenticationRevalidationState;
  retry: () => void;
  session: {
    absoluteExpiresAt: string;
    idleExpiresAt: string;
    user: AuthUser;
  } | null;
  setUserEnabled: (
    accountId: string,
    enabled: boolean,
  ) => Promise<AuthUser>;
  updateUser: (
    accountId: string,
    payload: AdminUserUpdate,
  ) => Promise<AuthUser>;
}

export const AuthenticationContext =
  createContext<AuthenticationState | null>(null);

export function useAuthentication(): AuthenticationState {
  const value = useContext(AuthenticationContext);
  if (value === null) {
    throw new Error('useAuthentication must be used inside AuthProvider.');
  }
  return value;
}

export function isAdministrator(role: AccountRole | undefined): boolean {
  return role === 'admin';
}
